import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  GetCarouselBarByCMS,
  GetCountdownByCMS,
  GetScheduleByCMS
} from '@/services/CMSApi'
import {
  CarouselBarSlidesProps,
  CountdownBannerProps,
  ImageCMSProps,
  LinkProps,
  SchedulesProps
} from '@/types/cms'
import { isDayWithinRange } from '@/utils/isDayWithinRange'

const CountdownsContext = React.createContext({} as Context)

export function useCountdownsContext() {
  return useContext(CountdownsContext)
}

export const CountdownsContextProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [countdowns, setCountdownValues] = useState<CountdownConfig[]>([])
  const [carouselSlides, setCarouselSlidesValues] = useState<CarouselConfig[]>(
    []
  )
  const [schedules, setSchedulesValues] = useState<ScheduleConfig[]>([])

  const checkCountdownCMS = useCallback(async () => {
    const countdownCMS = await GetCountdownByCMS()

    if (!countdownCMS) return

    const updatedCountdownsCMS = [] as CountdownConfig[]

    countdownCMS.forEach((item: CountdownBannerProps) => {
      let periodActive = {} as Period

      item.schedulePromo.displayPeriod.forEach((period: Period) => {
        const ativePeriod = isDayWithinRange({
          start: period.startAt,
          end: period.endAt
        })

        if (!ativePeriod) return

        periodActive = {
          startAt: period.startAt,
          endAt: period.endAt
        }

        if (ativePeriod) {
          updatedCountdownsCMS.push({
            countdownId: item.countdownId,
            period: periodActive,
            text: item.text,
            link: item.link,
            textPeriodColor: item.textPeriodColor,
            bgRoundedColor: item.bgRoundedColor,
            textRoundedColor: item.textRoundedColor,
            backgroundColor: item.backgroundColor
          })
        }
      })
    })

    setCountdownValues(updatedCountdownsCMS)
  }, [])

  const checkCarouselSlidesCMS = useCallback(async () => {
    const carouselSlidesCMS = await GetCarouselBarByCMS()

    if (!carouselSlidesCMS) return

    const updatedCarouselSlidesCMS = [] as CarouselConfig[]

    carouselSlidesCMS.forEach((item: CarouselBarSlidesProps) => {
      let periodActive = {} as Period

      item.schedulePromo &&
        item.schedulePromo.displayPeriod.forEach((period: Period) => {
          const ativePeriod = isDayWithinRange({
            start: period.startAt,
            end: period.endAt
          })

          if (!ativePeriod) return

          return (periodActive = {
            startAt: period.startAt,
            endAt: period.endAt
          })
        })

      return updatedCarouselSlidesCMS.push({
        carouselBarId: item.carouselBarId,
        period: item.schedulePromo ? periodActive : undefined,
        image: item.image,
        text: item.text,
        link: item.link,
        textHighlightColor: item.textHighlightColor,
        bgRoundedColor: item.bgRoundedColor,
        textRoundedColor: item.textRoundedColor,
        backgroundColor: item.backgroundColor
      })
    })

    setCarouselSlidesValues(updatedCarouselSlidesCMS)
  }, [])

  const checkScheduleCMS = useCallback(async () => {
    const scheduleCMS = await GetScheduleByCMS()

    if (!scheduleCMS) return

    const updatedSchedulesCMS = [] as ScheduleConfig[]

    scheduleCMS.forEach((item: SchedulesProps) => {
      let periodActive = {} as Period

      item.displayPeriod.forEach((period: Period) => {
        const ativePeriod = isDayWithinRange({
          start: period.startAt,
          end: period.endAt
        })

        if (!ativePeriod) return

        periodActive = {
          startAt: period.startAt,
          endAt: period.endAt
        }

        if (ativePeriod) {
          updatedSchedulesCMS.push({
            id: item.name,
            period: periodActive
          })
        }
      })
    })

    setSchedulesValues(updatedSchedulesCMS)
  }, [])

  useEffect(() => {
    checkCountdownCMS()
  }, [checkCountdownCMS])

  useEffect(() => {
    checkCarouselSlidesCMS()
  }, [checkCarouselSlidesCMS])

  useEffect(() => {
    checkScheduleCMS()
  }, [checkScheduleCMS])

  const countdownsByID = useMemo(() => {
    const map = new Map<string, CountdownConfig>()

    if (!countdowns) return map

    countdowns.forEach((item) => {
      map.set(item.countdownId, item)
    })

    return map
  }, [countdowns])

  const activeCountdowns = useMemo(() => {
    const list = new Set<string>()

    countdownsByID.forEach(({ period }, id) => {
      const isActive = isDayWithinRange({
        start: period.startAt,
        end: period.endAt
      })

      if (isActive) {
        list.add(id)
      }
    })

    return list
  }, [countdownsByID])

  const schedulesByID = useMemo(() => {
    const map = new Map<string, ScheduleConfig>()

    if (!schedules) return map
    schedules.forEach((item) => {
      map.set(item.id, item)
    })
    return map
  }, [schedules])

  const activePromo = useMemo(() => {
    const list = new Set<string>()

    schedulesByID.forEach(({ period }, id) => {
      const isActive = isDayWithinRange({
        start: period.startAt,
        end: period.endAt
      })

      if (isActive) {
        list.add(id)
      }
    })

    return list
  }, [schedulesByID])

  useEffect(() => {
    const midnight = new Date()
    midnight.setHours(0, 0, 0)
    midnight.setDate(midnight.getDate() + 1)

    const timeUntilMidnight = midnight.getTime() - new Date().getTime()

    const updateCountdowns = setTimeout(() => {
      checkCountdownCMS()
      checkCarouselSlidesCMS()
      checkScheduleCMS()
    }, timeUntilMidnight)

    return () => {
      clearTimeout(updateCountdowns)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CountdownsContext.Provider
      value={{
        countdownsByID,
        activeCountdowns,
        activePromo,
        carouselSlides
      }}
    >
      {children}
    </CountdownsContext.Provider>
  )
}

interface Period {
  endAt: string
  startAt: string
}

export interface CountdownConfig {
  countdownId: string
  text: string
  period: Period
  link: LinkProps | null
  textPeriodColor: string
  bgRoundedColor: string
  textRoundedColor: string
  backgroundColor: string
}

export interface CarouselConfig {
  carouselBarId: string
  text: string
  image: ImageCMSProps
  period?: Period
  link: LinkProps | null
  textHighlightColor: string
  bgRoundedColor: string
  textRoundedColor: string
  backgroundColor: string
}

export interface ScheduleConfig {
  id: string
  period: Period
}

export interface Context {
  activeCountdowns: Set<string>
  activePromo: Set<string>
  countdownsByID: Map<string, CountdownConfig>
  carouselSlides: CarouselConfig[]
}
